import { useAuth } from '../../../../contexts/AuthContext';
import {
  buildBusinessCardPayload,
  fetchBusinessCard,
  handleUploadError,
  postUpdatedCard,
  processUploadedData
} from './useScannerDataProcessing';
import useScannerState from './useScannerState';
import { trackBusinessCardUsage } from '../../../../services/userRequestService';
import { checkSufficientCredits } from '../../../../services/creditService';
import { compressImage } from '../../../../utils/imageCompression';

export default function useScannerWorkflow() {
  const {
    selectedImage,
    setSelectedImage,
    imagePreview,
    setImagePreview,
    fieldGroups,
    setFieldGroups,
    primaryFieldValues,
    setPrimaryFieldValues,
    additionalFields,
    setAdditionalFields,
    isScanning,
    setIsScanning,
    scanError,
    setScanError,
    showRateLimitModal,
    setShowRateLimitModal,
    rateLimitMessage,
    setRateLimitMessage,
    showInsufficientCreditsModal,
    setShowInsufficientCreditsModal,
    creditBalance,
    setCreditBalance,
    uploadRef,
    activeRequestIdRef,
    resetDetailsState,
    scrollToUpload,
    handlePrimaryFieldChange,
    handleAdditionalFieldLabelChange,
    handleAdditionalFieldValueChange,
    handleAddAdditionalField,
    handleRemoveAdditionalField
  } = useScannerState();

  const { user, isAuthenticated } = useAuth();

  const getUsernameForSubmission = () => {
    try {
      if (user) {
        return user.username || user.email || user.full_name || 'guest';
      }
    } catch (error) {
      /* ignore */
    }
    return 'guest';
  };

  const uploadAndScan = async (file) => {
    activeRequestIdRef.current += 1;
    const requestId = activeRequestIdRef.current;
    setIsScanning(true);
    setScanError('');

    try {
      // Check credit balance if user is authenticated
      if (isAuthenticated) {
        try {
          const { hasCredits, balance } = await checkSufficientCredits();
          setCreditBalance(balance);

          if (!hasCredits) {
            setShowInsufficientCreditsModal(true);
            setIsScanning(false);
            return;
          }
        } catch (creditError) {
          console.warn(
            '[Scanner] Failed to check credit balance:',
            creditError.message
          );
          // Continue with upload if credit check fails
        }
      }

      // Compress image before upload to prevent mobile memory issues
      let imageToUpload = file;
      try {
        console.log('[Scanner] Compressing image before upload...');
        imageToUpload = await compressImage(file, {
          maxSizeMB: 0.8, // Target 800KB
          maxWidthOrHeight: 1920, // Max dimension
          initialQuality: 0.8, // Starting quality
        });
        console.log('[Scanner] Image compressed successfully');
      } catch (compressionError) {
        console.warn(
          '[Scanner] Image compression failed, using original:',
          compressionError.message
        );
        // Continue with original file if compression fails
      }

      const form = new FormData();
      form.append('username', getUsernameForSubmission());
      form.append('image', imageToUpload, imageToUpload.name || 'business_card.jpg');
      form.append('is_logged_in', isAuthenticated ? 'true' : 'false');

      const data = await fetchBusinessCard({
        formData: form,
        setScanError,
        setIsScanning,
        setShowRateLimitModal,
        setRateLimitMessage
      });
      processUploadedData({
        data,
        requestId,
        activeRequestIdRef,
        setFieldGroups,
        setPrimaryFieldValues,
        setAdditionalFields,
        setImagePreview,
        setScanError
      });
    } catch (error) {
      handleUploadError({
        error,
        requestId,
        activeRequestIdRef,
        setScanError
      });
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setIsScanning(false);
      }
    }
  };

  const handleImageUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setScanError('');
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (loadEvent) => setImagePreview(loadEvent.target.result);
      reader.readAsDataURL(file);
      setTimeout(() => {
        uploadAndScan(file).catch(() => {});
      }, 0);
    }
  };

  const handleRemoveImage = () => {
    activeRequestIdRef.current += 1;
    setSelectedImage(null);
    setImagePreview(null);
    resetDetailsState();
    setIsScanning(false);
    setScanError('');
  };

  const handleConfirm = async () => {
    const username = getUsernameForSubmission();
    const payload = buildBusinessCardPayload({
      username,
      primaryFieldValues,
      additionalFields,
      imagePreview
    });

    try {
      await postUpdatedCard(payload, setScanError);
    } catch (error) {
      return;
    }

    // Track user request for credit usage
    try {
      await trackBusinessCardUsage({
        username,
        action: 'save_business_card',
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      // Don't block the user flow if tracking fails
      console.warn('[Scanner] Failed to track user request:', error.message);
    }

    try {
      const localPayload = {
        primaryFields: primaryFieldValues,
        additionalFields,
        savedAt: new Date().toISOString()
      };
      window.localStorage.setItem(
        'business-card:lastSaved',
        JSON.stringify(localPayload)
      );
    } catch (error) {
      /* ignore storage errors */
    }
    setSelectedImage(null);
    setImagePreview(null);
    resetDetailsState();
    setTimeout(scrollToUpload, 0);
  };

  const handleCancel = () => {
    setSelectedImage(null);
    setImagePreview(null);
    resetDetailsState();
    setTimeout(scrollToUpload, 0);
  };

  const handleRetryScan = () => {
    if (selectedImage) {
      uploadAndScan(selectedImage).catch(() => {});
    }
  };

  const handleDismissScanError = () => {
    setScanError('');
  };

  const shouldShowGlobalError = scanError
    && !imagePreview
    && fieldGroups.primaryFields.length === 0
    && additionalFields.length === 0;
  const hasExtractedData = fieldGroups.primaryFields.length > 0 || additionalFields.length > 0;

  return {
    uploadRef,
    imagePreview,
    isScanning,
    scanError,
    shouldShowGlobalError,
    hasExtractedData,
    showRateLimitModal,
    setShowRateLimitModal,
    rateLimitMessage,
    showInsufficientCreditsModal,
    setShowInsufficientCreditsModal,
    creditBalance,
    primaryFields: fieldGroups.primaryFields,
    additionalFields,
    primaryFieldValues,
    handleImageUpload,
    handleRemoveImage,
    handlePrimaryFieldChange,
    handleAdditionalFieldLabelChange,
    handleAdditionalFieldValueChange,
    handleAddAdditionalField,
    handleRemoveAdditionalField,
    handleConfirm,
    handleCancel,
    handleRetryScan,
    handleDismissScanError
  };
}

